import { useQuery } from '@tanstack/react-query';
import { DeviceManagers, User } from 'src/model/auth';
import { CommonResponse } from 'src/model/common';
import { AuthService } from 'src/services/api';
import { postRequest } from 'src/services/api/http';
import { URL_ENDPOINT } from 'src/utils/constants/service';

export const ME_KEY = 'me';
const QUERY_KEY_CHECK_EMAIL_EXIST = 'QUERY_KEY_CHECK_EMAIL_EXIST';
const QUERY_KEY_DEVICE_MANGER = 'QUERY_KEY_DEVICE_MANGER';
const QUERY_KEY_TOKEN_BY_API_KEY = 'QUERY_KEY_TOKEN_BY_API_KEY';

export const useQueryUserInfo = () => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, refetch } = useQuery<User, Error>({
    queryKey: [ME_KEY],
    queryFn: AuthService.getUserInfo,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new User(),
    placeholderData: (prev) => prev,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    refetch,
  };
};

export const useQueryCheckEmailExist = (email?: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_CHECK_EMAIL_EXIST, email ?? ''],
    queryFn: () => AuthService.checkEmailExists({ email: email ?? '' }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!email,
    initialData: {
      message: '',
      status_code: 0,
      warning: null,
    },
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDeviceManger = (params: Record<string, string | number>) => {
  const { isFetching, data, refetch } = useQuery({
    queryKey: [QUERY_KEY_DEVICE_MANGER, params],
    queryFn: () => AuthService.getDeviceMangers(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new DeviceManagers(),
  });
  return {
    isFetching,
    data,
    refetch,
  };
};

export const useQueryTokenByApiKey = (dto: Record<string, string>) => {
  return useQuery({
    queryKey: [QUERY_KEY_TOKEN_BY_API_KEY, dto],
    queryFn: () => AuthService.getTokenByApiKey(dto),
    retry: 0,
    initialData: new CommonResponse(),
    enabled: !!dto.api_key
  })
}

export const useQueryUsers = (dto: Record<string, any>) => {
  return useQuery({
    queryKey: ['QUERY_KEY_USERS', dto],
    queryFn: () => postRequest(URL_ENDPOINT.user.listUsers, dto) as Promise<IResponseList<Pick<IUser, 'uuid' | 'email'>>>,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}